import React, { useEffect, useState } from "react";
import Select, { MenuPlacement } from "react-select";
import { useParams } from "react-router-dom";
import useCustomBranding from "hooks/useCustomBranding";
import TextInput from "../../Components/Inputs/TextInput";

const customStyles = (transformY: string, height: string) => {
  const { primaryColor } = useCustomBranding();
  return {
    container: (provided: any) => ({
      ...provided,
      width: "100%",
      padding: "0px",
    }),
    control: (provided: any, state: any) => ({
      ...provided,
      borderRadius: "8px",
      width: "100%",
      borderColor: state.isFocused ? "transparent" : "transparent",
      backgroundColor: "red",
      boxShadow: null,
      outline: "none",
      "&:hover": {
        cursor: "text",
      },
    }),
    menu: (provided: any) => {
      return {
        ...provided,
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        minHeight: "100px",
        height: height,
        overflowY: "auto",
        overflowX: "hidden",
        zIndex: 10000,
        transform: transformY,
        width: "100%",
      };
    },
    menuList: (provided: any) => {
      return {
        ...provided,
        overflowX: "hidden",
        /* Add additional styles for MenuList here */
      };
    },
    option: (provided: any, state: any) => {
      return {
        ...provided,
        backgroundColor: state.isFocused
          ? "var(--primaryExtraLight, #EBF2F6)"
          : null,
        color: state.isFocused ? primaryColor : null,
        "&:hover": {
          backgroundColor: state.isFocused
            ? "var(--primaryExtraLight, #EBF2F6)"
            : "#f8f9fa",
          cursor: "pointer",
          whiteSpace: "normal",
          wordWrap: "break-word",
          overflowX: "hidden",
        },
        overflowWrap: "break-word",
      };
    },
    dropdownIndicator: (provided: any) => {
      return {
        ...provided,
        display: "none",
      };
    },
    indicatorsContainer: (provided: any) => {
      return {
        ...provided,
        cursor: "pointer",
        display: "none",
      };
    },
    multiValue: (provided: any) => {
      return {
        ...provided,
        backgroundColor: "#f1f5f8",
        borderRadius: "8px",
        display: "inline-flex",
        gap: "0.5rem",
        alignItems: "center",
        padding: "4px 8px",
      };
    },
    multiValueRemove: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: null,
      borderRadius: "0px",
      "&:hover": {
        backgroundColor: "transparent",
      },
      svg: {
        fill: "black",
      },
    }),
  };
};

const MultiValue = ({ data, remove }: any) => {
  const { communityId } = useParams<{ communityId: string }>();

  return (
    <div className="flex justify-center items-center mr-1 mb-1 flex-grow-0 flex-shrink-0 relative gap-1 p-1 rounded bg-primary/[0.08]">
      <div className="relative flex items-center justify-center flex-grow-0 flex-shrink-0 p-1">
        <p className="flex-grow-0 flex-shrink-0 text-xs text-center text-textMid ">
          {data.label}
        </p>
      </div>
      <button
        className="disabled:cursor-not-allowed"
        disabled={communityId !== undefined && data.id === communityId}
        onClick={() => remove(data)}
      >
        {" "}
        <svg
          width={16}
          height={16}
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="relative flex-grow-0 flex-shrink-0 w-4 h-4"
          preserveAspectRatio="xMidYMid meet"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.86128 4.86201C5.12163 4.60166 5.54374 4.60166 5.80409 4.86201L7.99935 7.05727L10.1946 4.86201C10.455 4.60166 10.8771 4.60166 11.1374 4.86201C11.3978 5.12236 11.3978 5.54447 11.1374 5.80482L8.94216 8.00008L11.1374 10.1953C11.3978 10.4557 11.3978 10.8778 11.1374 11.1382C10.8771 11.3985 10.455 11.3985 10.1946 11.1382L7.99935 8.94289L5.80409 11.1382C5.54374 11.3985 5.12163 11.3985 4.86128 11.1382C4.60093 10.8778 4.60093 10.4557 4.86128 10.1953L7.05654 8.00008L4.86128 5.80482C4.60093 5.54447 4.60093 5.12236 4.86128 4.86201Z"
            fill="#2C3236"
          />
        </svg>
      </button>
    </div>
  );
};

interface MultiSelectProps {
  inputValue?: string;
  onInputChange: (value: string) => void;
  options: { value: string; label: string }[];
  defaultValue: { value: string; label: string };
  isMulti: boolean;
  value: any;
  onChange: (selectedOptions: any) => void;
  transformY?: string;
  isDisable?: boolean;
  placeholder?: string;
  name?: string;
  formatOption?: any;
  maxWidth?: string;
  closeOnSelect?: boolean;
  onMenuOpen?: any;
  height?: string;
  className?: any;
  allowRemoveOption?: boolean;
  onMenuScrollToBottom?: any;
  isLoading?: boolean;
  errors?: any;
  handleBlur?: any;
  sx?: any;
  label?: string;
  editMode?: boolean;
  setFieldValue?: any;
  handleChange?: any;
  otherValue?: any;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  name,
  inputValue,
  onInputChange,
  options,
  defaultValue,
  isMulti,
  value,
  onChange,
  transformY,
  isDisable,
  placeholder,
  formatOption,
  onMenuOpen,
  closeOnSelect = true,
  height,
  maxWidth,
  className,
  allowRemoveOption = true,
  onMenuScrollToBottom,
  isLoading,
  errors,
  handleBlur,
  sx,
  label,
  editMode,
  setFieldValue,
  handleChange,
  otherValue,
}) => {
  const [menuPlacement, setMenuPlacement] = useState<MenuPlacement>("bottom");
  const [showClearIcon, setShowClearIcon] = useState(false);
  useEffect(() => {
    const menuElement = document.querySelector(".css-1pahdxg-control__menu");
    if (menuElement) {
      const windowHeight = window.innerHeight;
      const menuRect = menuElement.getBoundingClientRect();
      setMenuPlacement(menuRect.bottom > windowHeight ? "top" : "bottom");
    }
  }, [value, options]);

  const clearInput = () => {
    onInputChange("");
    setShowClearIcon(false);
  };

  const handleInputChange = (newValue: string) => {
    onInputChange(newValue);
    setShowClearIcon(newValue.length > 0);
  };

  const [initialValues] = useState<any>(defaultValue);

  const isOptionRemovable = (option: any) => {
    return !initialValues?.some(
      (initialValue: any) => initialValue.value === option.value
    );
  };

  // ✅ Move hook-using logic OUTSIDE of conditional return
  const computedStyles = {
    ...customStyles(transformY || "0", height || "unset"),
  
    multiValueRemove: (provided: any, props: any) => ({
      ...provided,
      display: allowRemoveOption
        ? "inherit"
        : isOptionRemovable(props.data)
          ? "flex"
          : "none",
    }),
  
    control: (provided: any, state: any) => ({
      ...provided,
      height,
      alignItems: "flex-start",
      border: "none",
      outline: "none",
      boxShadow: "none",
      zIndex: 100,
      backgroundColor: provided.backgroundColor,
  
      // On focus or hover: keep border and outline removed
      ...(state.isFocused && {
        border: "none",
        outline: "none",
        boxShadow: "none",
      }),
  
      '&:hover': {
        border: "none",
        outline: "none",
        boxShadow: "none",
      },
    }),
  
    placeholder: (provided: any) => ({
      ...provided,
      color: "#9B9FA5",
    }),
  
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
    }),
  
    ...sx,
  };
  
  const cleanedValue = Array?.isArray(value)
  ? value.filter((v) => v?.value && v?.label)
  : value?.value && value?.label
    ? value
    : null;
  return (
    <div className="relative w-full dark:bg-secondaryLight py-2">
      {label && (
        <p
          className={`${
            !editMode
              ? "text-secondaryMidLight"
              : "text-secondaryMid dark:text-caption font-medium"
          } text-sm text-left pb-2`}
        >
          {label}
        </p>
      )}

      {typeof editMode === "undefined" || editMode ? (
        <>
          {/* Show Select or text input based on "Other" selection */}
          <Select
            classNamePrefix="my-react-select"
            className={`${className} dark:bg-secondaryLight my-react-select-container`}
            isClearable
            inputValue={inputValue}
            name={name}
            onMenuOpen={onMenuOpen}
            onInputChange={handleInputChange}
            onBlur={(e) => handleBlur && handleBlur({ target: { name } })}
            options={options}
            defaultValue={defaultValue}
            placeholder={placeholder || "Select..."}
            menuPlacement={menuPlacement}
            isMulti={isMulti}
            value={cleanedValue}
            onChange={onChange}
            maxMenuHeight={200}
            isDisabled={isDisable}
            closeMenuOnSelect={closeOnSelect}
            styles={computedStyles}
            formatOptionLabel={formatOption}
            isSearchable
            noOptionsMessage={() => "No Record found"}
            onMenuScrollToBottom={onMenuScrollToBottom}
            isLoading={isLoading}
          />

          {showClearIcon && (
            <button
              className="absolute transform -translate-y-1/2 top-1/2 right-3"
              onClick={clearInput}
            >
              <svg
                width={20}
                height={20}
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M4.86128 4.86201C5.12163 4.60166 5.54374 4.60166 5.80409 4.86201L7.99935 7.05727L10.1946 4.86201C10.455 4.60166 10.8771 4.60166 11.1374 4.86201C11.3978 5.12236 11.3978 5.54447 11.1374 5.80482L8.94216 8.00008L11.1374 10.1953C11.3978 10.4557 11.3978 10.8778 11.1374 11.1382C10.8771 11.3985 10.455 11.3985 10.1946 11.1382L7.99935 8.94289L5.80409 11.1382C5.54374 11.3985 5.12163 11.3985 4.86128 11.1382C4.60093 10.8778 4.60093 10.4557 4.86128 10.1953L7.05654 8.00008L4.86128 5.80482C4.60093 5.54447 4.60093 5.12236 4.86128 4.86201Z"
                  fill="#2C3236"
                />
              </svg>
            </button>
          )}

          {/* Conditionally show "Other" input under select */}
          {((name === "fishery" &&
            Array.isArray(value) &&
            value.some((v) => v.label === "Other" || v.value === "Other")) ||
            (name === "tender" &&
              Array.isArray(value) &&
              value.some(
                (v) => v.label === "Other" || v.value === "Other"
              ))) && (
            <TextInput
              label=""
              type="text"
              name={name === "fishery" ? "otherFishery" : "otherTender"}
              setFieldValue={setFieldValue}
              error={""}
              placeholder={`Type other ${name}`}
              editMode={editMode}
              value={otherValue}
              touched={""}
              handleChange={handleChange}
              handleBlur={handleBlur}
            />
          )}
        </>
      ) : (
        <span className="text-sm text-gray-700">
          {(name === "fishery" || name === "tender") && Array.isArray(value)
            ? [
                ...value
                  .filter((v) => v.label !== "Other" && v.value !== "Other")
                  .map((v) => v.label),
                value.some((v) => v.label === "Other" || v.value === "Other")
                  ? otherValue
                  : null,
              ]
                .filter(Boolean)
                .join(", ")
            : Array.isArray(value)
              ? value.map((v) => v.label).join(", ")
              : value?.label || ""}
        </span>
      )}
    </div>
  );
};

export default MultiSelect;
